<template>
  <div class="markdown-editor">
    <div :id="editorId" class="editor-container"></div>
  </div>
</template>

<script>
export default {
  name: "MarkdownEditor",
  props: {
    value: {
      type: String,
      default: "",
    },
    height: {
      type: Number,
      default: 400,
    },
    placeholder: {
      type: String,
      default: "请输入内容...",
    },
  },
  data() {
    return {
      editor: null,
      editorId: `markdown-editor-${Math.random().toString(36).substring(2, 9)}`,
      isReady: false,
    };
  },
  mounted() {
    this.initEditor();
  },
  beforeDestroy() {
    if (this.editor) {
      // Properly cleanup the editor
      try {
        this.editor.remove();
      } catch (e) {
        console.warn("Error removing editor:", e);
      }
    }
  },
  watch: {
    value(newVal) {
      // Only update if editor is ready and value is different
      if (this.editor && this.isReady && newVal !== this.editor.getMarkdown()) {
        this.editor.setMarkdown(newVal || "");
      }
    },
    height(newHeight) {
      // Update editor height when prop changes
      if (this.editor && this.isReady) {
        this.editor.resize(null, newHeight);
      }
    },
  },
  methods: {
    initEditor() {
      this.$nextTick(() => {
        // Check if editormd is available
        if (typeof window.editormd === "undefined") {
          console.error("Editor.md library not loaded");
          return;
        }

        this.editor = window.editormd(this.editorId, {
          width: "100%",
          height: this.height,
          placeholder: this.placeholder,
          markdown: this.value || "",

          // Core functionality settings
          codeFold: true,
          saveHTMLToTextarea: true,
          searchReplace: true,
          htmlDecode: "style,script,iframe|on*",

          // Feature enablement
          emoji: true,
          taskList: true,
          tocm: true,
          tex: true,
          flowChart: true,
          sequenceDiagram: true,

          // Preview settings - enable real-time preview
          watch: true,
          preview: true,
          previewCodeHighlight: true,

          // Dialog settings
          dialogLockScreen: false,
          dialogShowMask: false,
          dialogDraggable: false,

          // Toolbar configuration - comprehensive set for article editing
          toolbarIcons: [
            "undo",
            "redo",
            "|",
            "bold",
            "del",
            "italic",
            "quote",
            "ucwords",
            "uppercase",
            "lowercase",
            "|",
            "h1",
            "h2",
            "h3",
            "h4",
            "h5",
            "h6",
            "|",
            "list-ul",
            "list-ol",
            "hr",
            "|",
            "link",
            "reference-link",
            "image",
            "code",
            "preformatted-text",
            "code-block",
            "table",
            "datetime",
            "emoji",
            "html-entities",
            "pagebreak",
            "|",
            "goto-line",
            "watch",
            "preview",
            "fullscreen",
            "clear",
            "search",
            "|",
            "help",
            "info",
          ],

          // Library path
          path: "/static/editor.md/lib/",

          // Plugin path for dialogs (image, link, etc.)
          pluginPath: "/static/editor.md/plugins/",

          // Image upload configuration
          imageUpload: true,
          imageFormats: ["jpg", "jpeg", "gif", "png", "bmp", "webp"],
          imageUploadURL:
            process.env.VUE_APP_BACKEND_URL + "/api/posts/editorUploadImage", // Update this to your actual upload endpoint
          crossDomainUpload: true,
          uploadCallbackURL:
            window.location.origin + "/static/editor.md/upload-callback.html",

          // Event handlers for two-way data binding
          onchange: () => {
            if (this.isReady) {
              const markdown = this.editor.getMarkdown();
              this.$emit("input", markdown);
            }
          },

          onload: () => {
            this.isReady = true;
            this.$emit("ready");

            // Set initial value if provided
            if (this.value) {
              this.editor.setMarkdown(this.value);
            }
          },

          // Additional event handlers for better UX
          onfullscreen: () => {
            this.$emit("fullscreen", true);
          },

          onfullscreenExit: () => {
            this.$emit("fullscreen", false);
          },
        });
      });
    },

    // Public methods for external control
    getMarkdown() {
      return this.editor ? this.editor.getMarkdown() : "";
    },

    getHTML() {
      return this.editor ? this.editor.getHTML() : "";
    },

    setMarkdown(markdown) {
      if (this.editor && this.isReady) {
        this.editor.setMarkdown(markdown || "");
      }
    },

    insertValue(value) {
      if (this.editor && this.isReady) {
        this.editor.insertValue(value);
      }
    },

    focus() {
      if (this.editor && this.isReady) {
        this.editor.focus();
      }
    },
  },
};
</script>

<style scoped>
.markdown-editor {
  width: 100%;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
}

.editor-container {
  width: 100%;
}

/* Ensure proper styling integration with Element UI */
.markdown-editor :deep(.editormd) {
  border: none;
}

.markdown-editor :deep(.editormd-toolbar) {
  border-bottom: 1px solid #dcdfe6;
}
</style>
